import { EnvProvider } from '@praxis/component-runtime-env'
import { AuthProvider, OnLoginCallback, useAuth } from '@praxis/component-auth'
import { useEffect, useState } from 'react'
import { ToastAlertProvider } from '@design-systems/canvas-ui'

import { safeTgtLogger, NO_STACK_TRACE } from './utils/safeTgtLogger'
import {
    QueryClient,
    QueryClientProvider,
    useQuery,
} from '@tanstack/react-query'

import { Constants } from './constants/constants'
import { UserProvider, useUser } from './components/user-context'
import Router from './router'
import { useMPMEnv } from './utils/env'
import { QueryKeys } from './constants/query-keys'
import { fetchNearbyLocations } from './queries/getNearbyLocationsQuery'
import { useAnalytics } from './utils/analytics'
import {
    NearbyLocationsProvider,
    useNearbyLocations,
} from './components/nearby-locations'

import FlagsProvider from './firebase/FirebaseRemoteConfig.provider'
import { FakeAuthenticationProvider } from 'utils/FakeAuthenticationProvider'

import './styles/index.scss'
import './app.css'
import { SplashScreen } from './components/splash-screen/splashScreen'
import { useUserDetails } from 'hooks/userDetails/useUserDetails'
import {
    setAnalyticsJobDetails,
    setAnalyticsLocationId,
} from 'utils/analyticsUserInfo'
import { LoginPlaceholder } from 'components/login-placeholder/loginPlaceholder'
import { useSetPreferences } from 'hooks/user-context/useSetPreferences'
import i18n from 'i18n'
import { transformLocation } from 'utils/useLocation'
import { setOtelUserId } from 'utils/otelUserAttributes'
import { ErrorAlert } from 'components/error-fallback'
import { useTranslation } from 'react-i18next'
import { TRANSLATION_KEYS as keys } from 'locales/translation-keys'
import { Unauthorized, TransientError } from 'pages/unauthorized'
import { ApiError } from 'queries/fetchData'
import {
    UnauthorizedReason,
    UnauthorizedReasonCode,
    isNetworkFailure,
    isUpstreamFailure,
    useLogUnauthorizedReason,
} from 'utils/unauthorizedReason'

import { useDeviceInfo } from 'hooks/utils/useDeviceInfo'
import { UrlParams } from 'enums/url-params'

import { usePrefersDarkMode } from 'hooks/utils/usePrefersDarkMode'
import { MPMLayout } from 'components/layout/layout'
import { initializeOpenTelemetry } from 'utils/initializeOpenTelemetry'

// Shows the retry screen if the runtime config fails to load
const handleConfigFailure = () => window.__mpmStartupBridge?.fail()

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: (failureCount, error) => {
                // Don't retry on 4xx — these are client errors, not transient
                if (
                    error instanceof Error &&
                    /\b4\d{2}\b/.test(error.message)
                ) {
                    return false
                }
                return failureCount < 2
            },
        },
    },
})

const OpenTelemetryInitializer = () => {
    const config = useMPMEnv()

    useEffect(() => {
        void initializeOpenTelemetry(config).catch((error: unknown) => {
            safeTgtLogger.warn('OpenTelemetry initialization failed', {
                error: {
                    message:
                        error instanceof Error
                            ? error.message
                            : 'OpenTelemetry initialization failed',
                    type: error instanceof Error ? error.name : 'Error',
                },
                event: {
                    action: 'otel_init_failed',
                    category: 'app_lifecycle',
                    outcome: 'failure',
                },
            })
        })
    }, [config])

    return null
}

const AppContent = () => {
    useEffect(() => {
        // Removes the html splash screen after the app has loaded and is ready to render
        window.__mpmStartupBridge?.ready()
    }, [])

    return (
        <>
            <OpenTelemetryInitializer />
            <QueryClientProvider client={queryClient}>
                <AppAuth />
            </QueryClientProvider>
        </>
    )
}

const App = () => {
    const isDark = usePrefersDarkMode()

    return (
        <div className={isDark ? 'c-foundations_darkmode' : undefined}>
            <EnvProvider
                commonConfig={{
                    praxis: { docsText: 'Explore the documentation' },
                }}
                configPath={
                    process.env.NODE_ENV === 'development'
                        ? '/config.json'
                        : '/app-environment'
                }
                onConfigFailure={handleConfigFailure}
            >
                <AppContent />
            </EnvProvider>
        </div>
    )
}

/**
 * This will examine the current `REACT_APP_ENV` and either use the custom
 * <FakeAuthenticationProvider /> or the standard <AuthProvider /> from Praxis.
 *
 * To run using the fake auth, set REACT_APP_ENV to 'fakeauth'. Example:
 * `REACT_APP_ENV=fakeauth npm run start`
 * @example
 * <AuthSwitch>
 *   <p>I'm authorized (for real or pretend)!</p>
 * </AuthSwitch>
 * @param children Components to render within the chosen auth context.
 * @returns JXS.Element
 */
const AuthSwitch = ({ children }: { children: React.ReactNode }) => {
    const env = useMPMEnv()
    const onLogin: OnLoginCallback = (err, session) => {
        if (err) {
            safeTgtLogger.error('Login failed', {
                error: {
                    message: err?.message || 'Login failed',
                    type: err?.name || 'LoginError',
                    stack_trace: err?.stack || NO_STACK_TRACE,
                },
                event: {
                    action: 'login_failed',
                    category: 'authentication',
                    outcome: 'failure',
                },
                user: {
                    id: session?.userInfo?.lanId || 'unknown',
                },
            })
        } else {
            const lanId = session?.userInfo?.lanId || 'unknown'
            setOtelUserId(lanId)
            safeTgtLogger.info('Login successful', {
                event: {
                    action: 'login_success',
                    category: 'authentication',
                    outcome: 'success',
                },
            })
        }
    }
    return (
        <>
            {process.env.REACT_APP_ENV === Constants.FAKE_AUTH ? (
                <FakeAuthenticationProvider>
                    {children}
                </FakeAuthenticationProvider>
            ) : (
                <AuthProvider
                    authorizationUrl={env.auth.authorizationUrl}
                    clientId={env.auth.clientId}
                    loginRedirect={window.location.origin}
                    logoutRedirect={env.auth.logoutRedirect}
                    logoutUrl={env.auth.logoutUrl}
                    onLogin={onLogin}
                    loadingIndicator={LoginPlaceholder}
                >
                    {children}
                </AuthProvider>
            )}
        </>
    )
}

const AppAuth = () => {
    return (
        <AuthSwitch>
            <UserProvider>
                <ToastAlertProvider>
                    <NearbyLocationsProvider>
                        <Router />
                    </NearbyLocationsProvider>
                </ToastAlertProvider>
            </UserProvider>
        </AuthSwitch>
    )
}

export interface IUserDetailsProps {
    children?: JSX.Element | JSX.Element[]
}
export const UserDetails = ({ children }: IUserDetailsProps) => {
    const env = useMPMEnv()
    const { session } = useAuth()
    const {
        location,
        greenfieldGroups,
        userAgent,
        setLocation,
        setGreenfieldGroups,
        setStoreLocationDetails,
        setLocationMapping,
        setUserLocationHierarchy,
        setLocationType,
    } = useUser()
    const { setNearbyLocations } = useNearbyLocations()
    const { isAuthenticated, login } = useAuth()

    const { lanId } = session?.userInfo ?? {
        lanId: '',
    }

    if (!isAuthenticated()) {
        login()
    }

    const { preferences, isReady: preferencesReady } = useSetPreferences()
    const { sendEvent } = useAnalytics()
    const { t } = useTranslation()
    const { data, isLoading, isError, error, refetch } = useUserDetails({
        env,
        accessToken: session?.accessToken ?? '',
        lanId,
    })
    const apiError = error instanceof ApiError ? error : undefined

    // A genuine end-user authz decision: the user is authenticated but the
    // service explicitly said they are forbidden (contractor, no entitlement).
    // This is NOT a transient failure — do not retry, show Unauthorized.
    const isForbiddenError = apiError?.status === 403

    // Transient / infra failures: anything that is NOT a deliberate authz
    // decision. This includes:
    //   - 401 from User Details (service-account token refresh blip, IAM issue)
    //   - 5xx / 502 / 504 (upstream failure, empty/non-JSON body)
    //   - Network timeout / offline (no HTTP response at all)
    // These must NOT trigger auto-logout; re-login won't help while the
    // upstream is still degraded. Show a retry screen instead.
    const isTransientError =
        isError && !isForbiddenError && apiError?.status !== 404

    const hasNoUserDetailsData = !isLoading && !isError && !data
    let unauthorizedReason: UnauthorizedReasonCode | null = null
    if (apiError?.status === 401) {
        unauthorizedReason = UnauthorizedReason.USER_DETAILS_401
    } else if (isForbiddenError) {
        unauthorizedReason = UnauthorizedReason.USER_DETAILS_FORBIDDEN
    } else if (isError && isUpstreamFailure(error)) {
        unauthorizedReason = UnauthorizedReason.USER_DETAILS_UPSTREAM_FAILURE
    } else if (isError && isNetworkFailure(error)) {
        unauthorizedReason = UnauthorizedReason.USER_DETAILS_NETWORK_FAILURE
    } else if (hasNoUserDetailsData) {
        unauthorizedReason = UnauthorizedReason.NO_USER_DETAILS_DATA
    }

    const { isZebraDevice } = useDeviceInfo(userAgent)
    const requestedLocation =
        new URLSearchParams(window.location.search).get(
            UrlParams.LOCATION_ID,
        ) ?? location.shortName

    useLogUnauthorizedReason(unauthorizedReason, {
        attempted_path: window.location.pathname,
        requested_location: requestedLocation,
        is_zebra: isZebraDevice,
        greenfield_groups_present: Boolean(greenfieldGroups?.length),
        ...(apiError && { upstream_status: apiError.status }),
    })

    const [hydratedUserDetailsData, setHydratedUserDetailsData] =
        useState<typeof data>()

    const { data: nearbyLocationsData } = useQuery(
        [QueryKeys.NEARBY_LOCATIONS],
        () => fetchNearbyLocations(env, session?.accessToken),
    )

    useEffect(() => {
        if (nearbyLocationsData?.locations) {
            setNearbyLocations(nearbyLocationsData.locations)
        }
    }, [nearbyLocationsData, setNearbyLocations])

    useEffect(() => {
        if (data?.user_details?.location_mapping?.location_id) {
            safeTgtLogger.info('User details loaded and processed', {
                event: {
                    action: 'user_details_loaded',
                    category: 'user_data',
                    outcome: 'success',
                },
                nonIndexedFields: {
                    locationId: data.user_details.location_mapping.location_id,
                    locationName:
                        data.user_details.location_mapping.location_name,
                },
            })
            setLocation({
                locationId:
                    data.user_details.location_mapping.location_id.toString(),
                shortName: transformLocation(
                    data.user_details.location_mapping.location,
                ),
                name: data.user_details.location_mapping.location_name,
                defaultStoreId:
                    data.user_details?.location_type === 'STR'
                        ? data.user_details?.location_mapping.location
                        : undefined,
            })
        }
        if (data?.greenfield_mpm_user_groups) {
            setGreenfieldGroups(data?.greenfield_mpm_user_groups)
        }
        if (data?.locations?.store_location_details) {
            setStoreLocationDetails(
                data.locations.store_location_details.map((location) => ({
                    ...location,
                    id: Number(location.id),
                })),
            )
        }
        if (data?.user_details) {
            setUserLocationHierarchy({
                region_id: data.user_details.region_id,
                group_id: data.user_details.group_id,
                district_id: data.user_details.district_id,
                store_id: data.user_details.store_id,
            })
            setLocationType(data.user_details.location_type)
        }
        if (data?.user_details?.location_mapping?.location_id) {
            setLocationMapping({
                location_id: Number(
                    data.user_details.location_mapping.location_id,
                ),
                location: data.user_details.location_mapping.location,
                location_name: data.user_details.location_mapping.location_name,
                entitled_for_non_store:
                    data.user_details.location_mapping.entitled_for_non_store,
            })
        }
        if (data) {
            setAnalyticsJobDetails(data.user_details?.job_details)
            setHydratedUserDetailsData(data)
        }
    }, [
        data,
        setLocation,
        setGreenfieldGroups,
        setStoreLocationDetails,
        setLocationMapping,
        setUserLocationHierarchy,
        setLocationType,
        lanId,
    ])

    useEffect(() => {
        setAnalyticsLocationId(location.locationId)
    }, [location.locationId])

    useEffect(() => {
        if (isError) {
            safeTgtLogger.error('User details error', {
                event: {
                    action: 'user_details_error',
                    category: 'user_data',
                    outcome: 'failure',
                },
                error: {
                    code: apiError?.status.toString(),
                    message: apiError?.message ?? 'Unknown error',
                    stack_trace: apiError?.stack ?? NO_STACK_TRACE,
                },
                nonIndexedFields: {
                    errorDetails: {
                        timestamp: new Date().toISOString(),
                    },
                },
            })
        }
        if (hasNoUserDetailsData) {
            safeTgtLogger.error('User details: no data', {
                event: {
                    action: 'user_details_no_data',
                    category: 'user_data',
                    outcome: 'failure',
                },
            })
        }
    }, [isError, hasNoUserDetailsData, apiError])

    useEffect(() => {
        if (preferencesReady) {
            const { user_contexts } = preferences
            let language = 'en' // default to English

            if (user_contexts?.length) {
                const [user_context] = user_contexts
                language = user_context?.app_context?.language || 'en'
            }

            const languagePreference = language?.startsWith('es') ? 'ES' : 'EN'

            void sendEvent({
                event: {
                    type: 'LOGIN',
                    value: 'language_preference',
                },
                client: {
                    language: languagePreference,
                    employee_login_id: lanId?.toUpperCase(),
                    version: process.env.APP_VERSION || '0.0.0',
                },
            })

            const landingPagePreference =
                user_contexts?.[0]?.app_context?.landingPage || 'not set'

            void sendEvent({
                event: {
                    type: 'LOGIN',
                    value: 'landing_page_preference',
                },
                client: {
                    landing_page: landingPagePreference,
                    employee_login_id: lanId?.toUpperCase(),
                    version: process.env.APP_VERSION || '0.0.0',
                },
            })

            if (i18n.language !== language) {
                i18n.changeLanguage(language).catch((error) => {
                    safeTgtLogger.warn('Failed to change language', {
                        error: {
                            message: error?.message || 'Language change failed',
                            type: error?.name || 'Error',
                        },
                        event: {
                            action: 'language_change_failed',
                            category: 'user_preferences',
                            outcome: 'failure',
                        },
                        nonIndexedFields: {
                            language,
                        },
                    })
                })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preferencesReady, lanId])
    // Update HTML lang attribute for screen readers when language changes
    useEffect(() => {
        const updateHtmlLang = () => {
            document.documentElement.lang = i18n.language
        }

        updateHtmlLang()

        i18n.on('languageChanged', updateHtmlLang)

        return () => {
            i18n.off('languageChanged', updateHtmlLang)
        }
    }, [])

    // Log if user is stuck on splash screen for more than 10 seconds
    useEffect(() => {
        if (!session) {
            const timer = setTimeout(() => {
                safeTgtLogger.warn(
                    'User stuck on splash screen - no session after 10s',
                    {
                        event: {
                            action: 'splash_screen_stuck',
                            category: 'app_lifecycle',
                            outcome: 'failure',
                        },
                        url: {
                            full: window.location.href,
                        },
                    },
                )
            }, 10000)
            return () => clearTimeout(timer)
        }
    }, [session])

    // returns SplashScreen before Authentication screen loads
    if (!session) {
        return <SplashScreen />
    }

    if (isError) {
        // 403 — user is a contractor or otherwise genuinely forbidden.
        // This is an authz decision, not a transient failure — do not retry.
        if (isForbiddenError) {
            return (
                <Unauthorized message={t(keys.unauthorized.forbiddenMessage)} />
            )
        }

        // 404 — the information needed to open MPM could not be loaded.
        if (apiError?.status === 404) {
            return (
                <MPMLayout breadcrumbsEnabled={false}>
                    <div className="hc8-mt-3x">
                        <ErrorAlert
                            error={
                                new Error(t(keys.unauthorized.notFoundTitle))
                            }
                            variant="custom"
                            pageViewName="user-not-found-error-alert"
                        >
                            <span>{t(keys.unauthorized.notFoundMessage)}</span>
                        </ErrorAlert>
                    </div>
                </MPMLayout>
            )
        }

        // All other errors (401, 5xx, 502/504, network timeout, empty/non-JSON)
        // are transient infrastructure failures, NOT genuine authz decisions.
        // Do NOT auto-logout: re-login won't help while the upstream is down.
        // Show a retry screen with clear, non-scary messaging instead.
        if (isTransientError) {
            return <TransientError onRetry={refetch} />
        }
    }

    if (isLoading) {
        return <LoginPlaceholder />
    }

    if (hasNoUserDetailsData) {
        return <Unauthorized message={t(keys.unauthorized.noUserDetailsData)} />
    }

    if (hydratedUserDetailsData !== data) {
        return <LoginPlaceholder />
    }

    return <FlagsProvider>{children}</FlagsProvider>
}

export default App
